import { atom, type Atom, type PrimitiveAtom, type WritableAtom } from 'jotai';

export type NamedTypeRef = {
	kind: 'NAMED';
	name: string;
};

export type ListTypeRef = {
	kind: 'LIST';
	ofType: TypeRef;
};

export type NonNullTypeRef = {
	kind: 'NON_NULL';
	ofType: TypeRef;
};

export type TypeRef = NamedTypeRef | ListTypeRef | NonNullTypeRef;

export type FieldArg = {
	name: string;
	description?: string | null;
	type: TypeRef;
	defaultValue?: string | null;
	isDeprecated?: boolean;
	deprecationReason?: string | null;
};

export type FieldDef = {
	name: string;
	description?: string | null;
	type?: TypeRef;
	args?: FieldArg[];
	isDeprecated?: boolean;
	deprecationReason?: string | null;
	inputType?: TypeRef;
};

export type TypeEntry = {
	name: string;
	kind: 'OBJECT' | 'INTERFACE' | 'UNION' | 'ENUM' | 'SCALAR' | 'INPUT_OBJECT' | 'UNKNOWN';
	description?: string | null;
	fields?: FieldDef[];
	interfaces?: string[];
	possibleTypes?: string[];
	values?: {
		name: string;
		description?: string | null;
		isDeprecated?: boolean;
		deprecationReason?: string | null;
	}[];
};

export type TypesByName = Record<string, TypeEntry>;

export type GraphQLSchemaVariant = 'internal' | 'external';

export const GraphQLSchemaLoadingAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const GraphQLSchemaErrorAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);
export const GraphQLSchemaTypesByNameAtom: PrimitiveAtom<TypesByName | null> & {
	init: TypesByName | null;
} = atom<TypesByName | null>(null);
export const GraphQLSchemaVariantAtom: PrimitiveAtom<GraphQLSchemaVariant> & {
	init: GraphQLSchemaVariant;
} = atom<GraphQLSchemaVariant>('internal');

export const GraphQLSchemaReloadKeyAtom: PrimitiveAtom<number> & {
	init: number;
} = atom(0);

// Action atom to flush schema state
export const FlushGraphQLSchemaAtom: WritableAtom<
	null,
	[
		opts?:
			| {
					andReload?: boolean;
			  }
			| undefined,
	],
	void
> & {
	init: null;
} = atom(null, (get, set, opts?: { andReload?: boolean }) => {
	set(GraphQLSchemaLoadingAtom, false);
	set(GraphQLSchemaErrorAtom, null);
	set(GraphQLSchemaTypesByNameAtom, null);
	set(GraphQLSchemaVariantAtom, 'internal');
	if (opts?.andReload) {
		set(GraphQLSchemaReloadKeyAtom, (k) => k + 1);
	}
});

export const GraphQLSchemaImplementorsByInterfaceAtom: Atom<Record<string, string[]> | null> = atom(
	(get) => {
		const map = get(GraphQLSchemaTypesByNameAtom);
		if (!map) return null as Record<string, string[]> | null;
		const res: Record<string, string[]> = {};
		for (const entry of Object.values(map)) {
			if (entry?.kind === 'OBJECT' && Array.isArray(entry.interfaces)) {
				for (const iface of entry.interfaces) {
					const key = iface.toLowerCase();
					(res[key] ||= []).push(entry.name);
				}
			}
		}
		return res;
	},
);
